import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import CalendarPicker from './CalendarPicker';

interface CustomField {
  id: number;
  column_name: string;
  type: string;
  options: string[] | null;
  isRequired: boolean;
  isShown: boolean;
  typeIds: number[] | null;
}

interface CustomFieldValue {
  id: number;
  custom_field_id: number;
  entity_id: number;
  value: any;
}

interface Props {
  tableName: string;
  entityId?: number;       // undefined = create mode (no auto-save)
  typeId?: number;
  onValuesChange?: (values: Record<number, any>) => void;  // called in create mode
  onFieldsLoaded?: (count: number) => void;
}

const labelOf = (s: string) =>
  s.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

export default function CustomFieldValues({ tableName, entityId, typeId, onValuesChange, onFieldsLoaded }: Props) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [values, setValues] = useState<Record<number, any>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    try {
      const { data: flds } = await axios.get(`fields/custom?table=${tableName}`);

      const visible: CustomField[] = (flds as CustomField[]).filter((f) => {
        if (!f.isShown) return false;
        if (typeId !== undefined && f.typeIds && f.typeIds.length > 0 && !f.typeIds.includes(typeId)) return false;
        return true;
      });
      setFields(visible);
      onFieldsLoaded?.(visible.length);

      if (entityId) {
        const { data: vals } = await axios.get(
          `custom-field-values?table=${tableName}&entityId=${entityId}`,
        );
        const map: Record<number, any> = {};
        for (const v of vals as CustomFieldValue[]) {
          map[v.custom_field_id] = v.value;
        }
        setValues(map);
        onValuesChange?.(map);
      } else {
        setValues({});
      }
    } catch {
      // silently skip
    }
  }, [tableName, entityId, typeId, onValuesChange, onFieldsLoaded]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (fieldId: number, value: any) => {
    if (!entityId) {
      // Create mode: collect locally, notify parent
      setValues((prev) => {
        const next = { ...prev, [fieldId]: value };
        onValuesChange?.(next);
        return next;
      });
      return;
    }
    // Edit mode: persist immediately
    setSaving((s) => ({ ...s, [fieldId]: true }));
    try {
      await axios.post('custom-field-values', { custom_field_id: fieldId, entity_id: entityId, value });
      setValues((prev) => {
        const next = { ...prev, [fieldId]: value };
        onValuesChange?.(next);
        return next;
      });
    } finally {
      setSaving((s) => ({ ...s, [fieldId]: false }));
    }
  }, [entityId, onValuesChange]);

  if (fields.length === 0) return null;

  return (
    <div>
      {fields.map((field) => {
        const val = values[field.id] ?? '';
        const isSaving = saving[field.id];

        return (
          <div key={field.id} className="flex flex-col my-[5px]">
            <label className="text-[10px] ml-[5px] text-[var(--text-color)]">
              {labelOf(field.column_name)}
              {field.isRequired && <span className="text-red-400 ml-1">*</span>}
            </label>

            {(field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'url') && (
              <input
                title={labelOf(field.column_name)}
                className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
                value={val}
                onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.value }))}
                onBlur={(e) => save(field.id, e.target.value)}
                disabled={isSaving}
              />
            )}

            {field.type === 'long_text' && (
              <textarea
                title={labelOf(field.column_name)}
                className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                rows={3}
                value={val}
                onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.value }))}
                onBlur={(e) => save(field.id, e.target.value)}
                disabled={isSaving}
              />
            )}

            {(field.type === 'number' || field.type === 'currency') && (
              <input
                title={labelOf(field.column_name)}
                className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                type="number"
                value={val}
                onChange={(e) => setValues((v) => ({ ...v, [field.id]: e.target.value }))}
                onBlur={(e) => save(field.id, e.target.value === '' ? null : Number(e.target.value))}
                disabled={isSaving}
              />
            )}

            {field.type === 'date' && (
              <CalendarPicker
                mode="single"
                variant="bare"
                output="string"
                className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                value={val ? String(val).slice(0, 10) : ''}
                onChange={(v) => save(field.id, v || null)}
                disabled={isSaving}
              />
            )}

            {field.type === 'checkbox' && (
              <div className="flex items-center mt-[4px]">
                <input
                  title={labelOf(field.column_name)}
                  type="checkbox"
                  checked={!!val}
                  onChange={(e) => save(field.id, e.target.checked)}
                  disabled={isSaving}
                  className="w-[16px] h-[16px] cursor-pointer"
                />
              </div>
            )}

            {field.type === 'select' && (
              <select
                title={labelOf(field.column_name)}
                className="edit-select p-[5px] rounded-[5px] edit-input text-[14px]"
                value={val ?? ''}
                onChange={(e) => save(field.id, e.target.value || null)}
                disabled={isSaving}>
                <option value="">—</option>
                {(field.options ?? []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}

            {field.type === 'multi_select' && (
              <div className="flex flex-wrap gap-[6px] mt-[4px]">
                {(field.options ?? []).map((opt) => {
                  const selected: string[] = Array.isArray(val) ? val : [];
                  const isOn = selected.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={isSaving}
                      onClick={() => {
                        const next = isOn ? selected.filter((v) => v !== opt) : [...selected, opt];
                        save(field.id, next);
                      }}
                      className={`text-[12px] px-[10px] py-[3px] rounded-full border transition-colors ${
                        isOn
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-transparent text-[var(--text-color)] border-[var(--border-color)]'
                      }`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
